import { Crown, ListOrdered, MapPin } from "lucide-react";

const BRACKET_LABEL = { W: "Winners", L: "Losers", F: "Grand Final", P3: "3rd Place" };

function MatchCard({ m, big }) {
  const side = (name, score, isWin) => (
    <div className="flex items-center justify-between px-3 py-1.5" style={{ background: isWin ? "rgba(34,197,94,0.14)" : "transparent" }}>
      <span className={`truncate ${big ? "text-sm" : "text-xs"}`} style={{ color: name && name !== "BYE" ? "var(--text)" : "var(--text-2)", fontWeight: isWin ? 700 : 500 }}>{name || "TBD"}</span>
      <span className={`mono font-bold ml-2 ${big ? "text-base" : "text-xs"}`} style={{ color: isWin ? "#22c55e" : "var(--text-2)" }}>{score}</span>
    </div>
  );
  const aWin = m.winner_name && m.winner_name === m.a_name;
  const bWin = m.winner_name && m.winner_name === m.b_name;
  return (
    <div className="rounded-lg glow-border overflow-hidden shrink-0" style={{ background: "var(--surface)", width: big ? 200 : 168 }} data-testid={`live-match-${m.match_id}`}>
      {side(m.a_name, m.a, aWin)}
      <div style={{ height: 1, background: "var(--border)" }} />
      {side(m.b_name, m.b, bWin)}
      {m.court_name && (
        <div data-testid={`live-match-court-${m.match_id}`} className="px-3 py-1 border-t text-[10px] mono uppercase tracking-wider flex items-center gap-1" style={{ borderColor: "var(--border)", background: "var(--bg)", color: "var(--primary-ink)" }}>
          <MapPin className="w-2.5 h-2.5" /> {m.court_name}
        </div>
      )}
    </div>
  );
}

function RRBody({ matches, standings }) {
  const rounds = [...new Set(matches.map((m) => m.round))].sort((a, b) => a - b);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-5" data-testid="live-rr">
      <div className="space-y-4">
        {rounds.map((r) => (
          <div key={r}>
            <div className="text-[10px] mono uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}>{matches.find((m) => m.round === r)?.round_label || `Round ${r}`}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{matches.filter((m) => m.round === r).map((m) => <MatchCard key={m.match_id} m={m} />)}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl glow-border p-4 h-fit" style={{ background: "var(--surface)" }}>
        <div className="font-display font-bold text-sm mb-3 flex items-center gap-2"><ListOrdered className="w-4 h-4 text-primary-c" /> Standings</div>
        {(standings || []).map((s, i) => (
          <div key={s.entrant_id} className="flex items-center justify-between text-sm py-0.5">
            <span className="flex items-center gap-2 truncate"><span className="mono text-primary-c w-5">#{i + 1}</span>{s.name}</span>
            <span className="mono font-bold">{s.wins}W</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ElimBody({ matches }) {
  const groups = ["W", "L", "F", "P3"].filter((g) => matches.some((m) => m.bracket === g));
  return (
    <div className="space-y-6" data-testid="live-elim">
      {groups.map((g) => {
        const gm = matches.filter((m) => m.bracket === g);
        const rounds = [...new Set(gm.map((m) => m.round))].sort((a, b) => a - b);
        return (
          <div key={g}>
            {g !== "W" && <div className="text-xs font-bold uppercase tracking-wider mb-2 text-primary-c">{BRACKET_LABEL[g]}</div>}
            <div className="flex gap-6 overflow-x-auto pb-2">
              {rounds.map((r) => (
                <div key={r} className="flex flex-col gap-3 justify-around">
                  <div className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{gm.find((m) => m.round === r)?.round_label || `Round ${r}`}</div>
                  {gm.filter((m) => m.round === r).map((m) => <MatchCard key={m.match_id} m={m} />)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function LiveBracket({ category }) {
  if (!category || (category.matches || []).length === 0) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Bracket not generated yet.</p>;
  const isRR = category.format === "round_robin";
  const body = (matches, standings) => (isRR ? <RRBody matches={matches} standings={standings} /> : <ElimBody matches={matches} />);

  if ((category.pool_count || 1) > 1 && Array.isArray(category.pools)) {
    const poolMatches = category.matches.filter((m) => (m.stage || "pool") !== "playoff");
    const playoffMatches = category.matches.filter((m) => m.stage === "playoff");
    return (
      <div className="space-y-8" data-testid="live-pools">
        {category.pools.map((p) => (
          <div key={p.pool_index} data-testid={`live-pool-${p.pool_index}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-lg text-primary-c">{p.label}</h4>
              {p.champion_name && <span className="text-xs glass rounded-full px-3 py-1 flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" style={{ color: "#eab308" }} /> {p.champion_name}</span>}
            </div>
            {body(poolMatches.filter((m) => (m.pool || 0) === p.pool_index), p.standings)}
          </div>
        ))}
        {category.has_playoff && playoffMatches.length > 0 && (
          <div className="pt-6 border-t" style={{ borderColor: "var(--border)" }} data-testid="live-playoff">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-lg flex items-center gap-2"><Crown className="w-5 h-5" style={{ color: "#eab308" }} /> Playoffs — Overall title</h4>
              {category.champion_name && <span className="text-xs glass rounded-full px-3 py-1 flex items-center gap-1.5">{category.champion_name}</span>}
            </div>
            {category.playoff_format === "round_robin"
              ? <RRBody matches={playoffMatches} standings={category.playoff_standings} />
              : <ElimBody matches={playoffMatches} />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {category.champion_name && (
        <div className="rounded-xl glow-border p-3 flex items-center gap-2" style={{ background: "rgba(34,197,94,0.10)" }}>
          <Crown className="w-5 h-5" style={{ color: "#eab308" }} /> <span className="font-display font-bold">{category.champion_name}</span> <span className="text-xs" style={{ color: "var(--text-2)" }}>champion</span>
        </div>
      )}
      {body(category.matches, category.standings)}
    </div>
  );
}
